import * as React from 'react';

export const CreatorContent: React.FC = () => (
  <div className='flex flex-col gap-6 text-left'>
    <div className='mb-4'>
      <h1 className='mb-2 font-sans text-24 font-bold leading-[32px] text-brand-800 md:text-32 md:leading-[40px]'>
        Creator Program Policy
      </h1>
    </div>

    <section>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Welcome to the D.ECHO Creator Program! This policy is for users who want to go beyond just
        chatting and start building their own Echoes for the community. Think of this as the
        &apos;Partnership Agreement&apos; for your digital twin. If you&apos;re accepted as an
        eligible Creator, you can share your Echo with other fans and earn a reward every time
        someone uses credits to interact with your content. You stay in control of your digital
        avatar and keep ownership of your original Echo setup, while we provide the platform to help
        you monetize your legacy. Below, you&apos;ll find the ground rules on how to stay eligible,
        how your earnings are calculated, and how we protect your rights as a creator.
        <br />
        <br />
        This Creator Program Policy (the &quot;Policy&quot;) is an integral part of the D.ECHO AI
        application&apos;s terms and conditions. By participating in the Creator Program, you agree
        to be bound by both this Policy and the underlying T&C. In the event of any conflict between
        this Policy and the T&C, this Policy shall prevail.
      </p>
    </section>

    <section>
      <h4 className='mb-3 font-sans text-18 font-bold text-brand-800 md:text-20'>
        Article 1 &mdash; Creator Eligibility
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        Users who create and publish Echoes or other original content for the community of Users of
        the Application are Creators. However, to earn rewards from their Echo&apos;s interactions,
        Creators must be officially accepted by the Company into its monetization program, subject
        to additional eligibility criteria established by the Company.
        <br />
        <br />
        Creators selected to be part of the monetization program must follow the D.ECHO Community
        Suitability Guidelines. While an interaction may not be illegal, the Company reserves the
        right to remove an Echo or restrict its monetization if it promotes misinformation or is
        otherwise harmful to the Application&apos;s brand and reputation.
        <br />
        <br />
        The Company reserves the right, in its sole discretion, to determine participation
        requirements for any Creator or revenue-sharing program, including but not limited to
        minimum usage thresholds, quality standards, and strict compliance with the applicable T&C
        and the Acceptable Use Policy.
        <br />
        <br />
        The Company may update eligibility criteria and quality standards at its sole discretion to
        ensure a safe environment for all Users.
        <br />
        <br />
        The Company may suspend, terminate, or refuse admission to the Creator Program at any time
        if a Creator:
      </p>
      <ul className='ml-6 mt-2 list-disc font-sans text-16 font-normal leading-7 text-gray-600'>
        <li>
          breaches this Creator Program Policy, the Application&apos;s T&C, or the Acceptable Use
          Policy;
        </li>
        <li>
          engages in fraudulent activity, self-dealing, artificial inflation of engagement metrics,
          or manipulation of the reward system;
        </li>
        <li>
          uploads Creator Content (as defined in article 3.2 below) that violates applicable law,
          infringes third-party rights, or is determined by the Company to be harmful, offensive, or
          inconsistent with the Application&apos;s community standards;
        </li>
        <li>
          fails to maintain the eligibility criteria set forth in the information communicated by
          the Company;
        </li>
        <li>
          is subject to legal or regulatory action that, in the Company&apos;s reasonable judgment,
          may adversely affect the reputation or operation of the Application.
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        No User shall have a guaranteed right to participate in the Creator Program, and
        participation does not create an employment, partnership, agency, or joint venture
        relationship between the Creator and the Company. Creators remain independent entities.
      </p>
    </section>

    <section>
      <h4 className='mb-3 font-sans text-18 font-bold text-brand-800 md:text-20'>
        Article 2 &mdash; Reward Value
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        For every interaction where other Users spend credits on a Creator&apos;s specific content,
        the Creator shall be eligible to receive a reward equal to up to twenty percent (20%) of the
        value of the credits effectively consumed by the other Users to interact with the
        Creator&apos;s Echo, as calculated by the Company&apos;s internal ledger.
        <br />
        <br />
        The Company may convert the applicable reward into credits, vouchers or other forms of
        in-Application-value, and may define payout thresholds, settlement intervals, and conversion
        mechanisms in separate policies or program terms, which shall form an integral part of these
        T&C. The minimum payout threshold is 10 EUR or its equivalent in other currencies.
        <br />
        <br />
        All credit usage, reward calculations, and User balances are determined solely on the basis
        of the Company&apos;s logs and measurement tools, which the Creator accepts as final and
        conclusive.
        <br />
        <br />
        The Company may temporarily suspend monetization, withhold, reverse, suspend or adjust
        rewards in cases of suspected fraud, abuse, self-dealing to inflate rewards, breach of these
        T&C, technical error, chargebacks initiated by Users, payment processing failures or refund
        to end-users, or where required by law or regulatory guidance. The Company shall not be
        liable for any loss of Creator earnings resulting from such actions taken in good faith.
        <br />
        <br />
        Creators are solely responsible for all taxes, duties, levies, and other governmental
        charges arising from Creator earnings, including but not limited to income tax, value-added
        tax (VAT), and any applicable withholding taxes. The Company may, where required by
        applicable law, withhold taxes from Creator earnings and remit such amounts to the
        appropriate tax authorities. Creators agree to provide all documentation reasonably
        requested by the Company to comply with tax reporting obligations.
        <br />
        <br />
        The Company uses third-party payment processors to facilitate Creator earnings payments.
        Creators must comply with the terms and verification requirements of such third-party
        processors. The Company is not liable for delays, errors, or failures caused by third-party
        payment processors.
      </p>
    </section>

    <section>
      <h4 className='mb-3 font-sans text-18 font-bold text-brand-800 md:text-20'>
        Article 3 &mdash; Licenses and Intellectual Property Rights
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        By participating as a Creator, you grant the Company a worldwide, non-exclusive,
        sublicensable, and royalty-free license to host, reproduce, distribute, display, perform,
        modify and adapt your content to other Users. This includes the right for the Company to use
        your Creator profile and Creator Content for marketing and promotional purposes.
        <br />
        <br />
        In accordance with the principle established in Article 10 of the Application&apos;s T&C,
        and as between the Company and the Creators, the Creators own all right, title, and interest
        in their original Echo configurations and generated content (&quot;Creator Content&quot;).
        <br />
        <br />
        In addition to the licenses granted to the Company under Article 10 of the
        Application&apos;s T&C Creators hereby grant the Company a perpetual, irrevocable,
        worldwide, and royalty-free license to use, reproduce, distribute, display, perform, modify,
        adapt and commercialize the Creator Content:
      </p>
      <ul className='ml-6 mt-2 list-disc font-sans text-16 font-normal leading-7 text-gray-600'>
        <li>to facilitate usage and interactions by other Users within the Application;</li>
        <li>
          to generate derivative marketing materials, including for cross-platform promotional use
          (e.g., TikTok, Instagram), ensuring your legacy reaches fans wherever they are.
        </li>
      </ul>
      <p className='mt-4 font-sans text-16 font-normal leading-24 text-gray-600'>
        Notwithstanding the personal and non-commercial use restriction in Article 10.7 of the
        Application&apos;s T&C, the Company hereby authorizes the Creators to use the Application
        for the specific commercial purpose of participating in Creator Programs and earning rewards
        as defined in this Creator Program Policy.
        <br />
        <br />
        By uploading Creator Content, you represent and warrant that:
      </p>
      <ul className='ml-6 mt-2 list-disc font-sans text-16 font-normal leading-7 text-gray-600'>
        <li>
          you own or have obtained all necessary rights, licenses, consents and permissions to grant
          the licenses set forth in this Article 3;
        </li>
        <li>
          your Creator Content does not and will not infringe, violate, or misappropriate any
          third-party intellectual property, privacy, image or publicity rights, or any other
          proprietary or personal rights;
        </li>
        <li>
          your Creator Content complies with all applicable laws and regulations and does not
          contain any material that is defamatory, obscene, harassing, threatening, or otherwise
          unlawful;
        </li>
        <li>
          you have obtained all necessary consents from any individuals depicted or referenced in
          your Creator Content.
        </li>
      </ul>
    </section>

    <section>
      <h4 className='mb-3 font-sans text-18 font-bold text-brand-800 md:text-20'>
        Article 4 &mdash; Program Exit and Data Removal
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        You may leave the Creator Program at any time by opting out in your settings or by
        initiating an account deletion as described in Article 13 of the T&C.
        <br />
        <br />
        If you choose to delete your account, all your Creator Content, including your Echo&apos;s
        configurations and Customer Data samples, will be queued for permanent removal.
        <br />
        <br />
        In accordance with Article 13.5 of the T&C, your data will be stored for a 30-day grace
        period to allow for reactivation. Once this period expires, the deletion is final and
        irreversible.
        <br />
        <br />
        Upon the permanent removal of your data after the 30-day window, the licenses granted to the
        Company in Article 3 of this Policy and Article 10 of the T&C shall terminate, except for
        data we are legally required to keep for tax or compliance reasons.
      </p>
    </section>

    <section>
      <h4 className='mb-3 font-sans text-18 font-bold text-brand-800 md:text-20'>
        Article 5 &mdash; Miscellaneous Obligations
      </h4>
      <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
        In accordance with the EU AI Act, Creators are strictly prohibited from using the
        Application to:
      </p>
      <ul className='ml-6 mt-2 list-disc font-sans text-16 font-normal leading-7 text-gray-600'>
        <li>
          Use any Echo to influence a person&apos;s behavior through imperceptible stimuli (visual,
          auditory, or cognitive) beyond their conscious awareness.
        </li>
        <li>
          Intentionally target or exploit the vulnerabilities of specific groups (due to age,
          disability, or socio-economic situation) to materially distort their behavior.
        </li>
        <li>
          Use manipulative or deceptive techniques designed to impair a person&apos;s ability to
          make an informed decision, leading them to take actions they would not otherwise have
          taken.
        </li>
      </ul>
    </section>
  </div>
);
